// src/store/slices/reportSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import reportApi from "api/reportApi";

export const fetchInventoryReport = createAsyncThunk(
  "report/fetchInventoryReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        page,
        page_size,
        search,
        date_range,
        start_date,
        end_date,
        // Advanced filters
        status,
        reorder_suggested,
        available_quantity_gte,
        available_quantity_lte,
        buffer_threshold_gte,
        buffer_threshold_lte,
        category,
        sub_category,
        // Any other filters
        ...restParams
      } = params;

      // Build the API params object
      const apiParams = {
        page,
        page_size,
        search,
        date_range,
        start_date,
        end_date,
        report_type: "current_summary",
        // Add advanced filters
        ...(status && { status }),
        ...(reorder_suggested && { reorder_suggested }),
        ...(available_quantity_gte && {
          available_quantity__gte: available_quantity_gte,
        }),
        ...(available_quantity_lte && {
          available_quantity__lte: available_quantity_lte,
        }),
        ...(buffer_threshold_gte && {
          buffer_threshold__gte: buffer_threshold_gte,
        }),
        ...(buffer_threshold_lte && {
          buffer_threshold__lte: buffer_threshold_lte,
        }),
        ...(category && { category }),
        ...(sub_category && { sub_category }),
        // Add any other params
        ...restParams,
      };

      const data = await reportApi.getInventoryReport(apiParams);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load inventory report"
      );
    }
  }
);

export const exportInventoryReport = createAsyncThunk(
  "report/exportInventoryReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getInventoryReport({
        ...filters,
        report_type: "current_summary",
        export: "csv",
      });

      const blob = response.data;

      // Ensure we actually have a Blob before trying to create a URL
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid file for export.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Inventory_Summary_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export inventory report"
      );
    }
  }
);

export const fetchStockMovementReport = createAsyncThunk(
  "report/fetchStockMovementReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getStockMovementReport(params);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load stock movement report"
      );
    }
  }
);

export const exportStockMovementReport = createAsyncThunk(
  "report/exportStockMovementReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getStockMovementReport({
        ...filters,
        report_type: "stock_movement",
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid file for export.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Stock_Movement_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export stock movement report"
      );
    }
  }
);

export const fetchLowStockReport = createAsyncThunk(
  "report/fetchLowStockReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getLowStockReport(params);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load low stock report"
      );
    }
  }
);

export const exportLowStockReport = createAsyncThunk(
  "report/exportLowStockReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getLowStockReport({
        ...filters,
        report_type: "low_stock",
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid file for export.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Low_Stock_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export low stock report"
      );
    }
  }
);

export const fetchNearExpiryReport = createAsyncThunk(
  "report/fetchNearExpiryReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getNearExpiryReport(params);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail ||
          "Failed to load near expiry / expired items report"
      );
    }
  }
);

export const exportNearExpiryReport = createAsyncThunk(
  "report/exportNearExpiryReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getNearExpiryReport({
        ...filters,
        report_type: "expired_near_expiry",
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid file for export.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Near_Expiry_Expired_Report_${new Date()
          .toISOString()
          .slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export near expiry report"
      );
    }
  }
);

export const fetchConsumptionTrendReport = createAsyncThunk(
  "report/fetchConsumptionTrendReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getConsumptionTrendReport(params);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load consumption trend report"
      );
    }
  }
);

export const exportConsumptionTrendReport = createAsyncThunk(
  "report/exportConsumptionTrendReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getConsumptionTrendReport({
        ...filters,
        report_type: "consumption_trend",
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid file for export.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Consumption_Trend_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export consumption trend report"
      );
    }
  }
);

export const fetchPurchaseHistoryReport = createAsyncThunk(
  "report/fetchPurchaseHistoryReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getPurchaseHistoryReport(params);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load purchase history report"
      );
    }
  }
);

export const exportPurchaseHistoryReport = createAsyncThunk(
  "report/exportPurchaseHistoryReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getPurchaseHistoryReport({
        ...filters,
        report_type: "purchase_history",
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid file for export.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Purchase_History_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export purchase history report"
      );
    }
  }
);

export const fetchPriceVariationReport = createAsyncThunk(
  "report/fetchPriceVariationReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getPriceVariationReport(params);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load price variation report"
      );
    }
  }
);

export const exportPriceVariationReport = createAsyncThunk(
  "report/exportPriceVariationReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getPriceVariationReport({
        ...filters,
        report_type: "price_variation",
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid file for export.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Price_Variation_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export price variation report"
      );
    }
  }
);

export const fetchDepartmentConsumptionReport = createAsyncThunk(
  "report/fetchDepartmentConsumptionReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getDepartmentConsumptionReport(params);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail ||
          "Failed to load department consumption report"
      );
    }
  }
);

export const exportDepartmentConsumptionReport = createAsyncThunk(
  "report/exportDepartmentConsumptionReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getDepartmentConsumptionReport({
        ...filters,
        report_type: "department_consumption",
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid file for export.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Department_Consumption_Report_${new Date()
          .toISOString()
          .slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export department consumption report"
      );
    }
  }
);

// NEW: Canteen Expiry Report thunk
export const fetchCanteenExpiryReport = createAsyncThunk(
  "report/fetchCanteenExpiryReport",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getCanteenExpiryReport(filters);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load canteen expiry report"
      );
    }
  }
);

// NEW: Consumption Trend
export const fetchConsumptionTrend = createAsyncThunk(
  "report/fetchConsumptionTrend",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await reportApi.getConsumptionTrend(filters);
      return response.data || { items: [], chartData: [], summary: {} };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load consumption trend"
      );
    }
  }
);

// NEW: Department-wise Consumption
export const fetchDepartmentConsumption = createAsyncThunk(
  "report/fetchDepartmentConsumption",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await reportApi.getDepartmentConsumption(filters);
      return response.data || { departments: [], summary: {} };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load department consumption"
      );
    }
  }
);

// Updated Purchase History thunk
export const fetchPurchaseHistory = createAsyncThunk(
  "report/fetchPurchaseHistory",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getPurchaseHistory(filters);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load purchase history"
      );
    }
  }
);

// NEW: Price Variation
export const fetchPriceVariation = createAsyncThunk(
  "report/fetchPriceVariation",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await reportApi.getPriceVariation(filters);
      return response.data || { items: [], summary: {} };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load price variation"
      );
    }
  }
);

// Standardized thunk names to ...Report
// Add this new thunk
// Add this thunk (similar to exportDepartments in departmentSlice)
// export const exportAssetReport = createAsyncThunk(
//   'report/exportAssetReport',
//   async ({ type, filters = {} }, { rejectWithValue }) => {
//     try {
//       const blob = await reportApi.getAssetReport({
//         ...filters,
//         type,
//         export: 'csv',
//         // No pagination for export
//       }).then(res => res.data);

//       // Trigger download
//       const url = URL.createObjectURL(blob);
//       const a = document.createElement('a');
//       a.href = url;
//       a.download = `${type.replace('_', ' ')} Report ${new Date().toISOString().slice(0, 10)}.csv`;
//       document.body.appendChild(a);
//       a.click();
//       document.body.removeChild(a);
//       URL.revokeObjectURL(url);

//       return { success: true };
//     } catch (err) {
//       return rejectWithValue('Export failed');
//     }
//   }
// );

// src/store/slices/reportSlice.js
// src/store/slices/reportSlice.js

// src/store/slices/reportSlice.js (export thunk - simplified & safe)
export const exportAssetReport = createAsyncThunk(
  "report/exportAssetReport",
  async ({ type, filters = {} }, { rejectWithValue }) => {
    try {
      // The API now returns the full axios response object for exports
      const response = await reportApi.getAssetReport({
        ...filters,
        type,
        export: "csv",
      });

      // The blob is in response.data
      const blob = response.data;

      // Ensure we actually have a Blob before trying to create a URL
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid file for export.");
      }

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const tabNames = {
        asset_master: "Asset Master",
        allocation: "Asset Allocation",
        idle: "Idle Assets",
        lifecycle: "Asset Lifecycle Cost",
        warranty: "Warranty Expiry",
      };
      const tabName =
        tabNames[type] ||
        type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

      link.setAttribute(
        "download",
        `${tabName}_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();

      // Clean up by removing the link and revoking the object URL
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(err.message || "Failed to export report");
    }
  }
);

export const fetchAssetMasterReport = createAsyncThunk(
  "report/fetchAssetMasterReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await reportApi.getAssetReport({
        ...params,
        type: "asset_master",
      });
      return response;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load asset master report"
      );
    }
  }
);

export const fetchNewAssetLifecycleReport = createAsyncThunk(
  "report/fetchNewAssetLifecycleReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await reportApi.getNewAssetLifecycleReport({
        ...params,
        report_type: "it_assignment_inventory_life_cycle", // Your specific type
      });
      return response; // API returns { summary, results, pagination }
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load asset lifecycle report"
      );
    }
  }
);

export const fetchAssetAllocationReport = createAsyncThunk(
  "report/fetchAssetAllocationReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await reportApi.getAssetReport({
        ...params,
        type: "allocation",
      });
      return response;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load asset allocation report"
      );
    }
  }
);

export const fetchIdleAssetsReport = createAsyncThunk(
  "report/fetchIdleAssetsReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await reportApi.getAssetReport({
        ...params,
        type: "idle",
      });
      return response;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load idle assets report"
      );
    }
  }
);

export const fetchAssetLifecycleReport = createAsyncThunk(
  "report/fetchAssetLifecycleReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await reportApi.getAssetReport({
        ...params,
        type: "lifecycle",
      });
      return response;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load asset lifecycle report"
      );
    }
  }
);

export const fetchWarrantyExpiryReport = createAsyncThunk(
  "report/fetchWarrantyExpiryReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await reportApi.getAssetReport({
        ...params,
        type: "warranty",
      });
      return response;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load warranty expiry report"
      );
    }
  }
);

// all break report/////////////////////////////
export const fetchBreakReport = createAsyncThunk(
  "report/fetchBreakReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getBreakReport(params);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load break time report"
      );
    }
  }
);

export const exportBreakReport = createAsyncThunk(
  "report/exportBreakReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getBreakReport({
        ...filters,
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid CSV file.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Break_Time_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(err.message || "Failed to export break report");
    }
  }
);

export const fetchBreakTotalDurationReport = createAsyncThunk(
  "report/fetchBreakTotalDurationReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getReport({
        ...params,
        report_type: "total_duration",
      });
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail ||
          "Failed to load break total duration report"
      );
    }
  }
);

export const exportBreakTotalDurationReport = createAsyncThunk(
  "report/exportBreakTotalDurationReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getReport({
        ...filters,
        export: "csv",
      });
      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid CSV file.");
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Break_Total_Duration_Report_${new Date()
          .toISOString()
          .slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export break total duration report"
      );
    }
  }
);

export const fetchBreakIrregularActivityReport = createAsyncThunk(
  "report/fetchBreakIrregularActivityReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      // Use the specific API function for irregular activity report
      const data = await reportApi.getBreakIrregularActivityReport(params);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load irregular activity report"
      );
    }
  }
);

export const exportBreakIrregularActivityReport = createAsyncThunk(
  "report/exportBreakIrregularActivityReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getBreakIrregularActivityReport({
        ...filters,
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid CSV file.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Irregular_Activity_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export irregular activity report"
      );
    }
  }
);

export const fetchBreakTimeDeviationReport = createAsyncThunk(
  "report/fetchBreakTimeDeviationReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getBreakTimeDeviationReport(params);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail ||
          "Failed to load break time deviation report"
      );
    }
  }
);

export const exportBreakTimeDeviationReport = createAsyncThunk(
  "report/exportBreakTimeDeviationReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getBreakTimeDeviationReport({
        ...filters,
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid CSV file.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Time_Deviation_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export time deviation report"
      );
    }
  }
);

////////////////////////////

// Updated Break Summary thunk
export const fetchBreakSummary = createAsyncThunk(
  "report/fetchBreakSummary",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getBreakSummary(filters);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load break summary"
      );
    }
  }
);

// NEW: Total Break Duration
export const fetchBreakDuration = createAsyncThunk(
  "report/fetchBreakDuration",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await reportApi.getBreakDuration(filters);
      return response.data || { employees: [], summary: {} };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load break duration"
      );
    }
  }
);

// NEW: Irregular Breaks
export const fetchIrregularBreaks = createAsyncThunk(
  "report/fetchIrregularBreaks",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await reportApi.getIrregularBreaks(filters);
      return response.data || { irregularities: [], summary: {} };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load irregular breaks"
      );
    }
  }
);

// NEW: Time Deviation
export const fetchTimeDeviation = createAsyncThunk(
  "report/fetchTimeDeviation",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await reportApi.getTimeDeviation(filters);
      return response.data || { deviations: [], summary: {} };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load time deviation"
      );
    }
  }
);

// NEW: Tickets Overview Report thunk
export const fetchTicketsOverview = createAsyncThunk(
  "report/fetchTicketsOverview",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getTicketsOverview(filters);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load tickets overview"
      );
    }
  }
);

// NEW: Department-wise Ticket Volume thunk
export const fetchDepartmentTickets = createAsyncThunk(
  "report/fetchDepartmentTickets",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getDepartmentTickets(filters);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load department ticket volume"
      );
    }
  }
);

// NEW: Recurring Issues thunk
export const fetchRecurringIssues = createAsyncThunk(
  "report/fetchRecurringIssues",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getRecurringIssues(filters);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load recurring issues"
      );
    }
  }
);

// NEW: Handler Productivity thunk
export const fetchHandlerProductivity = createAsyncThunk(
  "report/fetchHandlerProductivity",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getHandlerProductivity(filters);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load handler productivity"
      );
    }
  }
);

// NEW: Get Ticket Details thunk
export const fetchTicketReport = createAsyncThunk(
  "report/fetchTicketReport",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getTicketReport(filters);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load ticket report"
      );
    }
  }
);

// NEW: TAT Compliance
export const fetchTatCompliance = createAsyncThunk(
  "report/fetchTatCompliance",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await reportApi.getTatCompliance(filters);
      return response.data || { compliance: [], departments: [], summary: {} };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load TAT compliance"
      );
    }
  }
);

// NEW: Escalation History thunk
export const fetchEscalationHistory = createAsyncThunk(
  "report/fetchEscalationHistory",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getEscalationHistory(filters);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load escalation history"
      );
    }
  }
);

export const fetchTicketDetails = createAsyncThunk(
  "report/fetchTicketDetails",
  async (ticketId, { rejectWithValue }) => {
    try {
      const data = await reportApi.getTicketDetails(ticketId);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load ticket details"
      );
    }
  }
);

// NEW: Canteen Orders thunk
export const fetchCanteenOrders = createAsyncThunk(
  "report/fetchCanteenOrders",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getCanteenOrders(filters);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load canteen orders"
      );
    }
  }
);

// NEW: Canteen Orders Stats thunk
export const fetchCanteenOrdersStats = createAsyncThunk(
  "report/fetchCanteenOrdersStats",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getCanteenOrdersStats(filters);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load canteen orders stats"
      );
    }
  }
);

// NEW: Popular & Least Popular Items
export const fetchPopularItems = createAsyncThunk(
  "report/fetchPopularItems",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await reportApi.getPopularItems(filters);
      return response.data || { popular: [], leastPopular: [], summary: {} };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load popular items"
      );
    }
  }
);

// NEW: Canteen Finance
export const fetchCanteenFinance = createAsyncThunk(
  "report/fetchCanteenFinance",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await reportApi.getCanteenFinance(filters);
      return response.data || { collections: [], expenses: [], summary: {} };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load canteen finance"
      );
    }
  }
);

// NEW: Ingredient Stock thunk
export const fetchIngredientStock = createAsyncThunk(
  "report/fetchIngredientStock",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getIngredientStock(filters);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load ingredient stock"
      );
    }
  }
);

// asstes all report

export const fetchAssetReport = createAsyncThunk(
  "report/fetchAssetReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      // The 'type' parameter is crucial for determining which report to fetch.
      const { type, ...otherParams } = params;
      const response = await reportApi.getAssetReport({ type, ...otherParams });
      return { type, data: response.data }; // Return type along with data
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || `Failed to load ${params.type} report`
      );
    }
  }
);

/////////////////////////////canteen -------------------

export const fetchOrderSummaryReport = createAsyncThunk(
  "report/fetchOrderSummaryReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getCanteenOrderReport({ ...params });
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load order summary report"
      );
    }
  }
);

export const exportOrderSummaryReport = createAsyncThunk(
  "report/exportOrderSummaryReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getCanteenOrderReport({
        ...filters,
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid CSV file.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Order_Summary_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export order summary report"
      );
    }
  }
);

export const fetchPopularItemsReport = createAsyncThunk(
  "report/fetchPopularItemsReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getPopularItemsReport({ ...params });
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load popular items report"
      );
    }
  }
);

export const exportPopularItemsReport = createAsyncThunk(
  "report/exportPopularItemsReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getPopularItemsReport({
        ...filters,
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid CSV file.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Popular_Items_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export popular items report"
      );
    }
  }
);

export const fetchExpenseCollectionReport = createAsyncThunk(
  "report/fetchExpenseCollectionReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getCanteenExpenseReport({ ...params });
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail ||
          "Failed to load expense & collection report"
      );
    }
  }
);

export const exportExpenseCollectionReport = createAsyncThunk(
  "report/exportExpenseCollectionReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getCanteenExpenseReport({
        ...filters,
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid CSV file.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Expense_Collection_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export expense collection report"
      );
    }
  }
);

export const fetchIngredientStockReport = createAsyncThunk(
  "report/fetchIngredientStockReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getIngredientStockReport({ ...params });
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load ingredient stock report"
      );
    }
  }
);

export const exportIngredientStockReport = createAsyncThunk(
  "report/exportIngredientStockReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getIngredientStockReport({
        ...filters,
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid CSV file.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Ingredient_Stock_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export ingredient stock report"
      );
    }
  }
);

/////////////////////////all ticket ////////////////////////////

export const fetchTicketOpenClosedReport = createAsyncThunk(
  "report/fetchTicketOpenClosedReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getOpenClosedReport(params);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail ||
          "Failed to load open/closed tickets report"
      );
    }
  }
);

export const exportTicketOpenClosedReport = createAsyncThunk(
  "report/exportTicketOpenClosedReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getOpenClosedReport({
        ...filters,
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid CSV file.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Open_Closed_Tickets_Report_${new Date()
          .toISOString()
          .slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export open/closed tickets report"
      );
    }
  }
);

export const fetchTicketTatComplianceReport = createAsyncThunk(
  "report/fetchTicketTatComplianceReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getTATReport(params);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load TAT compliance report"
      );
    }
  }
);

export const exportTicketTatComplianceReport = createAsyncThunk(
  "report/exportTicketTatComplianceReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getTATReport({
        ...filters,
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid CSV file.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `TAT_Compliance_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export TAT compliance report"
      );
    }
  }
);

export const fetchTicketEscalationReport = createAsyncThunk(
  "report/fetchTicketEscalationReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getEscalationReport({ ...params });
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load escalation report"
      );
    }
  }
);

export const exportTicketEscalationReport = createAsyncThunk(
  "report/exportTicketEscalationReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getEscalationReport({
        ...filters,
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid CSV file.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Escalation_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export escalation report"
      );
    }
  }
);

export const fetchTicketDepartmentVolumeReport = createAsyncThunk(
  "report/fetchTicketDepartmentVolumeReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getDepartmentVolumeReport({ ...params });
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load department volume report"
      );
    }
  }
);

export const exportTicketDepartmentVolumeReport = createAsyncThunk(
  "report/exportTicketDepartmentVolumeReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getDepartmentVolumeReport({
        ...filters,
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid CSV file.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Department_Volume_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export department volume report"
      );
    }
  }
);

export const fetchTicketRecurringIssuesReport = createAsyncThunk(
  "report/fetchTicketRecurringIssuesReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getRecurringIssuesReport({ ...params });
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load recurring issues report"
      );
    }
  }
);

export const exportTicketRecurringIssuesReport = createAsyncThunk(
  "report/exportTicketRecurringIssuesReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getRecurringIssuesReport({
        ...filters,
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid CSV file.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Recurring_Issues_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export recurring issues report"
      );
    }
  }
);

export const fetchTicketHandlerProductivityReport = createAsyncThunk(
  "report/fetchTicketHandlerProductivityReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await reportApi.getHandlerProductivityReport({ ...params });
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail ||
          "Failed to load handler productivity report"
      );
    }
  }
);

export const exportTicketHandlerProductivityReport = createAsyncThunk(
  "report/exportTicketHandlerProductivityReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      const response = await reportApi.getHandlerProductivityReport({
        ...filters,
        export: "csv",
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid CSV file.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Handler_Productivity_Report_${new Date()
          .toISOString()
          .slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(
        err.message || "Failed to export handler productivity report"
      );
    }
  }
);


export const fetchWorkTaskReport = createAsyncThunk(
  "report/fetchWorkTaskReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        page,
        page_size,
        search,
        date_range,
        start_date,
        end_date,
        // Advanced filters
        department_id,
        assignee_id,
        status,
        priority,
        // Any other filters
        ...restParams
      } = params;

      // Build the API params object
      const apiParams = {
        page,
        page_size,
        search,
        date_range,
        start_date,
        end_date,
        // Add advanced filters
       ...(department_id && { department_id }),
        ...(assignee_id && { assignee_id }),
        ...(status && { status }),
        ...(priority && { priority }),
        // Add any other params
        ...restParams,
      };

      const data = await reportApi.getWorkTaskReport(apiParams);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load work task report"
      );
    }
  }
);


export const exportWorkTaskReport = createAsyncThunk(
  "report/exportWorkTaskReport",
  async ({ filters = {} }, { rejectWithValue }) => {
    try {
      // Use the new dedicated export method (if you added it)
      const response = await reportApi.exportWorkTaskCsv(filters);

      // OR if you kept it in getWorkTaskReport:
      // const response = await reportApi.getWorkTaskReport({ ...filters, export: "csv" });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The server did not return a valid CSV file.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Work_Task_Report_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Export error:", err);
      return rejectWithValue(err.message || "Failed to export work task report");
    }
  }
);



// export const exportWorkTaskReport = createAsyncThunk(
//   "report/exportWorkTaskReport",
//   async ({ filters = {} }, { rejectWithValue }) => {
//     try {
//       const response = await reportApi.getWorkTaskReport({
//         ...filters,
//         export: "csv",
//       });

//       const blob = response.data;
//       if (!(blob instanceof Blob)) {
//         throw new Error("The server did not return a valid file for export.");
//       }

//       const url = window.URL.createObjectURL(blob);
//       const link = document.createElement("a");
//       link.href = url;
//       link.setAttribute(
//         "download",
//         `Work_Task_Report_${new Date().toISOString().slice(0, 10)}.csv`
//       );
//       document.body.appendChild(link);
//       link.click();
//       document.body.removeChild(link);
//       window.URL.revokeObjectURL(url);

//       return { success: true };
//     } catch (err) {
//       console.error("Export error:", err);
//       return rejectWithValue(err.message || "Failed to export work task report");
//     }
//   }
// );

/////////////////////////////////////////////////////

const reportSlice = createSlice({
  name: "report",
  initialState: {
    inventoryReport: { summary: {}, results: [] },
    stockMovementReport: { data: { summary: {}, results: [] } },
    lowStockReport: { data: { summary: {}, results: [] } },
    nearExpiryReport: { data: { summary: {}, results: [] } },
    consumptionTrendReport: { data: { summary: {}, results: [] } },
    purchaseHistoryReport: { data: { summary: {}, results: [] } },
    priceVariationReport: { data: { summary: {}, results: [] } },
    departmentConsumptionReport: { data: { summary: {}, results: [] } },
    canteenExpiryReport: {
      counts: {
        total: 0,
        expired: 0,
        about_to_expire: 0,
      },
      results: [],
    },
    consumptionTrend: { items: [], chartData: [], summary: {} },
    departmentConsumption: { departments: [], summary: {} },
    purchaseHistory: [],
    priceVariation: { items: [], summary: {} },
    assetReports: {
      asset_master: { summary: {}, results: [] },
      allocation: { summary: {}, results: [] },
      idle: { summary: {}, results: [] },
      lifecycle: { summary: {}, results: [] },
      warranty: { summary: {}, results: [] },
    },
newAssetLifecycle: {
  summary: {},
  data: [],
  pagination: { total_pages: 1, count: 0 },
},
    assetMaster: {
      summary: {},
      data: [],
      pagination: { count: 0, total_pages: 1 },
    },
    assetAllocation: {
      summary: {},
      data: [],
      pagination: { count: 0, total_pages: 1 },
    },
    idleAssets: {
      summary: {},
      data: [],
      pagination: { count: 0, total_pages: 1 },
    },
    assetLifecycle: {
      summary: {},
      data: [],
      pagination: { count: 0, total_pages: 1 },
    },
    warrantyExpiry: {
      summary: {},
      data: [],
      pagination: { count: 0, total_pages: 1 },
    },
    breakReport: { data: { summary: {}, results: [] }, pagination: null },
    breakTotalDurationReport: {
      data: { summary: {}, results: [] },
      pagination: null,
    },
    breakIrregularActivityReport: {
      data: { summary: {}, results: [] },
      pagination: null,
    },
    breakTimeDeviationReport: {
      data: { summary: {}, results: [] },
      pagination: null,
    },

    breakSummary: [],
    // breakSummary: { breaks: [], summary: {} },
    breakDuration: { employees: [], summary: {} },
    irregularBreaks: { irregularities: [], summary: {} },
    timeDeviation: { deviations: [], summary: {} },
    ticketReport: {
      overview: {
        total: 0,
        open: 0,
        in_progress: 0,
        resolved: 0,
        ack_pending: 0,
        closed: 0,
      },
      tabs: {
        department_wise: [],
        recurring_issues: [],
        handler_productivity: [],
      },
      tickets: {
        data: [],
        pagination: {
          next: null,
          previous: null,
          count: 0,
          current_page: 1,
          total_pages: 1,
          page_size: 10,
          max_page_size: 1000,
        },
        errors: [],
        success: true,
        status: 200,
      },
    },
    tatCompliance: { compliance: [], departments: [], summary: {} },
    escalations: { escalations: [], summary: {} },
    escalationHistory: { data: [], ticketDetails: null },
    departmentTickets: { departments: [], summary: {} },
    recurringIssues: { issues: [], summary: {} },
    handlerProductivity: { handlers: [], summary: {} },

    orderSummaryReport: {
      data: { summary: {}, results: [] },
      pagination: null,
    },
    popularItemsReport: {
      data: { summary: {}, most_popular: [], least_popular: [], results: [] },
      pagination: null,
    }, // NEW – dedicated
    expenseCollectionReport: {
      data: { summary: {}, results: [] },
      pagination: null,
    }, // NEW – dedicated
    ingredientStockReport: {
      data: { summary: {}, results: [] },
      pagination: null,
    },

    // all ticket

    ticketOpenClosedReport: {
      data: { summary: {}, results: [] },
      pagination: null,
    },
    ticketTatComplianceReport: {
      data: { summary: {}, results: [] },
      pagination: null,
    },
    ticketEscalationReport: {
      data: { summary: {}, results: [] },
      pagination: null,
    },
    ticketDepartmentVolumeReport: {
      data: { summary: {}, results: [] },
      pagination: null,
    },
    ticketRecurringIssuesReport: {
      data: { summary: {}, results: [] },
      pagination: null,
    },
    ticketHandlerProductivityReport: {
      data: { summary: {}, results: [] },
      pagination: null,
    },


    /// work task 

workTaskReport: {
  data: [],
  pagination: {
    next: null,
    previous: null,
    count: 0,
    current_page: 1,
    total_pages: 1,
    page_size: 10,
    max_page_size: 1000,
  },
  statistics: {
    total_tasks: 0,
    in_progress: 0,
    on_hold: 0,
    cancelled: 0,
    completed: 0,
    overdue: 0,
  },
  message: "",
  errors: [],
  success: true,
  status: 200,
},
    /////

    ////////////////

    canteenOrders: [],
    canteenOrdersStats: {
      orders: {
        total: 0,
        today: 0,
        pending: 0,
        preparing: 0,
        delivered: 0,
        cancelled: 0,
      },
      financials: {
        total_collection: 0,
        total_expense: 0,
        net_profit: 0,
      },
      menu_stats: {
        most_popular: {
          menu__id: "",
          menu__name: "",
          order_count: 0,
        },
        least_popular: {
          menu__id: "",
          menu__name: "",
          order_count: 0,
        },
      },
      ratings: {
        average_rating: 0,
      },
    },
    
    popularItems: { popular: [], leastPopular: [], summary: {} },
    canteenFinance: { collections: [], expenses: [], summary: {} },
    ingredientStock: [],
    pagination: {
      count: 0,
      current_page: 1,
      page_size: 10,
      total_pages: 1,
      next: null,
      previous: null,
    },
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const handleFulfilled = (state, action, key) => {
      const payload = action.payload;
      const pageSize = action.meta.arg.page_size || 10;

      state[key] = {
        summary: payload.data?.summary || {},
        data: payload.data?.results || [],
        pagination: {
          count:
            payload.pagination?.count || payload.data?.results?.length || 0,
          total_pages:
            payload.pagination?.total_pages ||
            Math.ceil(
              (payload.pagination?.count ||
                payload.data?.results?.length ||
                0) / pageSize
            ),
        },
      };
    };

    builder

      // ===================================================================
      // INVENTORY REPORT REDUCERS
      // ===================================================================
      .addCase(fetchInventoryReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      // .addCase(fetchInventoryReport.fulfilled, (state, action) => {
      //   state.isLoading = false;
      //   state.inventoryReport = action.payload || { summary: {}, results: [] };
      // })

      .addCase(fetchInventoryReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.inventoryReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: {},
        };
      })

      .addCase(fetchInventoryReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW Stock Movement cases

      .addCase(fetchStockMovementReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      // .addCase(fetchStockMovementReport.fulfilled, (state, action) => {
      //   state.isLoading = false;
      //   state.stockMovementReport = action.payload || {
      //     data: { summary: {}, results: [] },
      //   };
      // })
      .addCase(fetchStockMovementReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stockMovementReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: {},
        };
      })
      .addCase(fetchStockMovementReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW Low Stock cases

      .addCase(fetchLowStockReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLowStockReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lowStockReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: {},
        };
      })
      .addCase(fetchLowStockReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW Near Expiry cases

      .addCase(fetchNearExpiryReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNearExpiryReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.nearExpiryReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: {},
        };
      })
      .addCase(fetchNearExpiryReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW Consumption Trend cases

      .addCase(fetchConsumptionTrendReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchConsumptionTrendReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.consumptionTrendReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: {},
        };
      })
      .addCase(fetchConsumptionTrendReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW: Department Consumption
      .addCase(fetchDepartmentConsumption.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDepartmentConsumption.fulfilled, (state, action) => {
        state.isLoading = false;
        state.departmentConsumption = action.payload;
      })
      .addCase(fetchDepartmentConsumption.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW Purchase History cases

      .addCase(fetchPurchaseHistoryReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPurchaseHistoryReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.purchaseHistoryReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: {},
        };
      })
      .addCase(fetchPurchaseHistoryReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchPurchaseHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW: Price Variation

      .addCase(fetchPriceVariationReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPriceVariationReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.priceVariationReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: {},
        };
      })
      .addCase(fetchPriceVariationReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW Department Consumption cases

      .addCase(fetchDepartmentConsumptionReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDepartmentConsumptionReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.departmentConsumptionReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: {},
        };
      })
      .addCase(fetchDepartmentConsumptionReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // ===================================================================
      // UNIFIED ASSET REPORTS REDUCER
      // ===================================================================
      .addCase(fetchAssetReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAssetReport.fulfilled, (state, action) => {
        state.isLoading = false;
        const { type, data } = action.payload;
        // Update the specific report type in the state
        if (state.assetReports[type]) {
          state.assetReports[type] = data;
        }
      })
      .addCase(fetchAssetReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
    // Asset Master
    builder
      .addCase(fetchAssetMasterReport.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAssetMasterReport.fulfilled, (state, action) => {
        state.isLoading = false;
        handleFulfilled(state, action, "assetMaster");
      })
      .addCase(fetchAssetMasterReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Asset Allocation
      .addCase(fetchAssetAllocationReport.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAssetAllocationReport.fulfilled, (state, action) => {
        state.isLoading = false;
        handleFulfilled(state, action, "assetAllocation");
      })
      .addCase(fetchAssetAllocationReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Idle Assets
      .addCase(fetchIdleAssetsReport.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchIdleAssetsReport.fulfilled, (state, action) => {
        state.isLoading = false;
        handleFulfilled(state, action, "idleAssets");
      })
      .addCase(fetchIdleAssetsReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // new asset lifecycle
 .addCase(fetchNewAssetLifecycleReport.pending, (state) => {
  state.isLoading = true;
})
.addCase(fetchNewAssetLifecycleReport.fulfilled, (state, action) => {
  state.isLoading = false;
  state.newAssetLifecycle = {
    summary: action.payload.data?.summary || {},
    data: action.payload.data?.results || [],
    pagination: action.payload.pagination || { total_pages: 1, count: 0 },
  };
})
.addCase(fetchNewAssetLifecycleReport.rejected, (state, action) => {
  state.isLoading = false;
  state.error = action.payload;
})
      // Asset Lifecycle
      .addCase(fetchAssetLifecycleReport.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAssetLifecycleReport.fulfilled, (state, action) => {
        state.isLoading = false;
        handleFulfilled(state, action, "assetLifecycle");
      })
      .addCase(fetchAssetLifecycleReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Warranty Expiry
      .addCase(fetchWarrantyExpiryReport.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchWarrantyExpiryReport.fulfilled, (state, action) => {
        state.isLoading = false;
        handleFulfilled(state, action, "warrantyExpiry");
      })
      .addCase(fetchWarrantyExpiryReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // all Break Report cases/////////////////////////

      .addCase(fetchBreakReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBreakReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.breakReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: {},
        };
      })
      .addCase(fetchBreakReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DEDICATED Total Duration Report

      .addCase(fetchBreakTotalDurationReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBreakTotalDurationReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.breakTotalDurationReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: {},
        };
      })
      .addCase(fetchBreakTotalDurationReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DEDICATED Irregular Activity Report

      // Inside extraReducers
      .addCase(fetchBreakIrregularActivityReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBreakIrregularActivityReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.breakIrregularActivityReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: {},
        };
      })
      .addCase(fetchBreakIrregularActivityReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DEDICATED Time Deviation Report
      // Inside extraReducers
      .addCase(fetchBreakTimeDeviationReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBreakTimeDeviationReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.breakTimeDeviationReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: {},
        };
      })
      .addCase(fetchBreakTimeDeviationReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      //////////////////////////////////////

      /////////////  all tikcet////////////////

      // DEDICATED Open/Closed Tickets Report
      .addCase(fetchTicketOpenClosedReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketOpenClosedReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.ticketOpenClosedReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: null,
        };
      })
      .addCase(fetchTicketOpenClosedReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DEDICATED TAT Compliance Report
      .addCase(fetchTicketTatComplianceReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketTatComplianceReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.ticketTatComplianceReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: null,
        };
      })
      .addCase(fetchTicketTatComplianceReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DEDICATED Escalation Report
      .addCase(fetchTicketEscalationReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketEscalationReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.ticketEscalationReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: null,
        };
      })
      .addCase(fetchTicketEscalationReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DEDICATED Department Volume Report

      .addCase(fetchTicketDepartmentVolumeReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketDepartmentVolumeReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.ticketDepartmentVolumeReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: null,
        };
      })
      .addCase(fetchTicketDepartmentVolumeReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DEDICATED Recurring Issues Report

      .addCase(fetchTicketRecurringIssuesReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketRecurringIssuesReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.ticketRecurringIssuesReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: null,
        };
      })
      .addCase(fetchTicketRecurringIssuesReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DEDICATED Handler Productivity Report
      .addCase(fetchTicketHandlerProductivityReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchTicketHandlerProductivityReport.fulfilled,
        (state, action) => {
          state.isLoading = false;
          state.ticketHandlerProductivityReport = action.payload || {
            data: { summary: {}, results: [] },
            pagination: null,
          };
        }
      )
      .addCase(
        fetchTicketHandlerProductivityReport.rejected,
        (state, action) => {
          state.isLoading = false;
          state.error = action.payload;
        }
      )

      ////////////////////////

      // Updated Break Summary case
      .addCase(fetchBreakSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBreakSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.breakSummary = action.payload || {
          data: [],
          pagination: {
            next: null,
            previous: null,
            count: 0,
            current_page: 1,
            total_pages: 1,
            page_size: 10,
            max_page_size: 1000,
          },
        };
      })
      .addCase(fetchBreakSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // work task

      // DEDICATED Work Task Report
.addCase(fetchWorkTaskReport.pending, (state) => {
  state.isLoading = true;
  state.error = null;
})
.addCase(fetchWorkTaskReport.fulfilled, (state, action) => {
  state.isLoading = false;
  const payload = action.payload || {};
  state.workTaskReport = {
    data: payload.data || [],
    pagination: payload.pagination || {
      next: null,
      previous: null,
      count: 0,
      current_page: 1,
      total_pages: 1,
      page_size: 10,
      max_page_size: 1000,
    },
    statistics: payload.statistics || {
      total_tasks: 0,
      in_progress: 0,
      on_hold: 0,
      cancelled: 0,
      completed: 0,
      overdue: 0,
    },
    message: payload.message || "",
    errors: payload.errors || [],
    success: payload.success ?? true,
    status: payload.status || 200,
  };
})
.addCase(fetchWorkTaskReport.rejected, (state, action) => {
  state.isLoading = false;
  state.error = action.payload;
})
      // NEW: Break Duration
      .addCase(fetchBreakDuration.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBreakDuration.fulfilled, (state, action) => {
        state.isLoading = false;
        state.breakDuration = action.payload;
      })
      .addCase(fetchBreakDuration.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW: Irregular Breaks
      .addCase(fetchIrregularBreaks.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIrregularBreaks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.irregularBreaks = action.payload;
      })
      .addCase(fetchIrregularBreaks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW: Time Deviation
      .addCase(fetchTimeDeviation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTimeDeviation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.timeDeviation = action.payload;
      })
      .addCase(fetchTimeDeviation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // === Ticket Report Cases ===
      .addCase(fetchTicketReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;

        const payload = action.payload || {};

        state.ticketReport = {
          overview: {
            total: payload.overview?.total || 0,
            open: payload.overview?.open || 0,
            in_progress: payload.overview?.in_progress || 0,
            resolved: payload.overview?.resolved || 0,
            ack_pending: payload.overview?.ack_pending || 0,
            closed: payload.overview?.closed || 0,
          },
          tabs: {
            department_wise: payload.tabs?.department_wise || [],
            recurring_issues: payload.tabs?.recurring_issues || [],
            handler_productivity: payload.tabs?.handler_productivity || [],
            tickets: {
              data: payload.tabs?.tickets?.data || [],
              pagination: payload.tabs?.tickets?.pagination || {
                next: null,
                previous: null,
                count: 0,
                current_page: 1,
                total_pages: 1,
                page_size: 10,
                max_page_size: 1000,
              },
              errors: payload.tabs?.tickets?.errors || [],
              success: payload.tabs?.tickets?.success ?? true,
              status: payload.tabs?.tickets?.status || 200,
            },
          },
        };
      })
      // NEW: TAT Compliance
      .addCase(fetchTatCompliance.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTatCompliance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tatCompliance = action.payload;
      })
      .addCase(fetchTatCompliance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW: Escalation History case
      .addCase(fetchEscalationHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEscalationHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.escalationHistory = action.payload || {
          data: [],
          pagination: {
            next: null,
            previous: null,
            count: 0,
            current_page: 1,
            total_pages: 1,
            page_size: 10,
            max_page_size: 1000,
          },
        };
      })
      .addCase(fetchEscalationHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // NEW: Ticket Details case
      .addCase(fetchTicketDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.ticketDetails = action.payload;
      })
      .addCase(fetchTicketDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // NEW: Department Tickets
      .addCase(fetchDepartmentTickets.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDepartmentTickets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.departmentTickets = action.payload;
      })
      .addCase(fetchDepartmentTickets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW: Recurring Issues
      .addCase(fetchRecurringIssues.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRecurringIssues.fulfilled, (state, action) => {
        state.isLoading = false;
        state.recurringIssues = action.payload;
      })
      .addCase(fetchRecurringIssues.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW: Handler Productivity
      .addCase(fetchHandlerProductivity.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchHandlerProductivity.fulfilled, (state, action) => {
        state.isLoading = false;
        state.handlerProductivity = action.payload;
      })
      .addCase(fetchHandlerProductivity.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW: Canteen Orders case
      .addCase(fetchCanteenOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCanteenOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.canteenOrders = action.payload || {
          data: [],
          pagination: {
            next: null,
            previous: null,
            count: 0,
            current_page: 1,
            total_pages: 1,
            page_size: 10,
            max_page_size: 1000,
          },
        };
      })
      .addCase(fetchCanteenOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW: Canteen Orders Stats case
      .addCase(fetchCanteenOrdersStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCanteenOrdersStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.canteenOrdersStats = action.payload || {
          orders: {
            total: 0,
            today: 0,
            pending: 0,
            preparing: 0,
            delivered: 0,
            cancelled: 0,
          },
          financials: {
            total_collection: 0,
            total_expense: 0,
            net_profit: 0,
          },
          menu_stats: {
            most_popular: {
              menu__id: "",
              menu__name: "",
              order_count: 0,
            },
            least_popular: {
              menu__id: "",
              menu__name: "",
              order_count: 0,
            },
          },
          ratings: {
            average_rating: 0,
          },
        };
      })
      .addCase(fetchCanteenOrdersStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW: Popular Items
      .addCase(fetchPopularItems.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPopularItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.popularItems = action.payload;
      })
      .addCase(fetchPopularItems.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW: Canteen Finance
      .addCase(fetchCanteenFinance.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCanteenFinance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.canteenFinance = action.payload;
      })
      .addCase(fetchCanteenFinance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // NEW: Ingredient Stock case
      .addCase(fetchIngredientStock.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIngredientStock.fulfilled, (state, action) => {
        state.isLoading = false;
        state.ingredientStock = action.payload || [];
      })
      .addCase(fetchIngredientStock.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      /// all canteen ////////////////////////////////////
      // DEDICATED Order Summary Report
      .addCase(fetchOrderSummaryReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderSummaryReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.orderSummaryReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: null,
        };
      })
      .addCase(fetchOrderSummaryReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // DEDICATED Popular Items Report

      .addCase(fetchPopularItemsReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPopularItemsReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.popularItemsReport = action.payload || {
          data: {
            summary: {},
            most_popular: [],
            least_popular: [],
            results: [],
          },
          pagination: null,
        };
      })
      .addCase(fetchPopularItemsReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // DEDICATED Expense vs Collection Report
      .addCase(fetchExpenseCollectionReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchExpenseCollectionReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.expenseCollectionReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: null,
        };
      })
      .addCase(fetchExpenseCollectionReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DEDICATED Ingredient Stock Report
      .addCase(fetchIngredientStockReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIngredientStockReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.ingredientStockReport = action.payload || {
          data: { summary: {}, results: [] },
          pagination: null,
        };
      })
      .addCase(fetchIngredientStockReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = reportSlice.actions;
export default reportSlice.reducer;
